// PrivacyPolicy.js
import React, { useEffect } from "react";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full min-h-screen bg-gray-100 p-8">
      <div className="max-w-5xl mx-auto bg-white p-8 rounded-md shadow-sm">
        <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>

        <p className="mb-4">
          <strong>Last Updated:</strong> March 10, 2026
        </p>

        <p className="mb-4">
          Ashlar Building Group LLC ("Company", "we", "us", or "our")
          values your privacy and is committed to protecting your
          personal information. This Privacy Policy explains how we
          collect, use, disclose, and safeguard your data when you
          access our website or use our services.
        </p>

        <p className="mb-4">
          By using our website or services (the "Service"), you consent
          to the practices described in this Privacy Policy. Your access
          to and use of the Website is conditional upon your acceptance
          of and compliance with this Privacy Policy. This Privacy Policy
          applies to everyone, including, but not limited to: visitors,
          users, and others, who wish to access or use the Website.
        </p>

        {/* Section 1 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          1. Definitions
        </h2>

        <p className="mb-2">
          <strong>Personal Data:</strong> Information that identifies or
          can reasonably be linked to an individual.
        </p>

        <p className="mb-2">
          <strong>Usage Data:</strong> Information collected automatically
          when using the Service.
        </p>

        <p className="mb-2">
          <strong>Service:</strong> The Ashlar Building Group website and
          related online services.
        </p>

        <p className="mb-2">
          <strong>Service Provider:</strong> A third-party organization
          that processes data on our behalf.
        </p>

        <p className="mb-2">
          <strong>You:</strong> The individual or entity using the
          Service.
        </p>

        {/* Section 2 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          2. Information We Collect
        </h2>

        <h3 className="font-semibold mb-2">
          2.1 Personal Information You Provide
        </h3>

        <p className="mb-2">
          When you submit our contact form, we collect your first name,
          last name, email address, phone number, city, project type,
          and project description. This information is used solely to
          respond to your inquiry and facilitate our services.
        </p>

        <h3 className="font-semibold mb-2 mt-4">
          2.2 Automatically Collected Information
        </h3>

        <p className="mb-2">
          We may automatically collect IP address, browser type, device
          information, pages visited, referral URLs, analytics data, and
          diagnostic information.
        </p>

        <h3 className="font-semibold mb-2 mt-4">
          2.3 Cookies and Tracking Technologies
        </h3>

        <p className="mb-2">
          We use cookies to improve functionality, analyze usage, and
          personalize your experience. You can manage cookies through
          your browser settings.
        </p>

        {/* Section 3 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          3. How We Use Your Information
        </h2>

        {/* Mobile Information Usage */}
        <h3 className="text-lg font-semibold mt-4 mb-2">
          Mobile Information Usage
        </h3>

        <p className="mb-4">
          No mobile information will be shared with third
          parties/affiliates for marketing/promotional purposes.
          Information sharing to subcontractors in support services,
          such as customer service, is permitted. All other use case
          categories exclude text messaging originator opt-in data and
          consent; this information will not be shared with any third
          parties.
        </p>

        {/* Information We Collect */}
        <h3 className="text-lg font-semibold mt-4 mb-2">
          Information We Collect
        </h3>

        <p className="mb-1">We may collect:</p>

        <ul className="list-disc list-inside mb-4 text-gray-700 space-y-1 ml-2">
          <li>Name</li>
          <li>Phone number</li>
          <li>Communication preferences</li>
        </ul>

        {/* How We Use Information */}
        <h3 className="text-lg font-semibold mt-4 mb-2">
          How We Use Information
        </h3>

        <ul className="list-disc list-inside mb-4 text-gray-700 space-y-1 ml-2">
          <li>To respond to inquiries</li>
          <li>To send service-related messages you opted into</li>
          <li>To provide customer support</li>
        </ul>

        {/* Data Sharing */}
        <h3 className="text-lg font-semibold mt-4 mb-2">
          Data Sharing
        </h3>

        <p className="mb-4">
          We do not share mobile information with third parties for
          marketing purposes. Data may only be shared if required by law
          or to comply with legal obligations.
        </p>

        {/* SMS Terms */}
        <h3 className="text-lg font-semibold mt-6 mb-3">
          SMS Terms
        </h3>

        <div className="space-y-4 text-gray-700">

          <div>
            <p>
              <strong>1- SMS Consent Communication:</strong> The
              information (including phone numbers) obtained as part of
              the SMS consent process will not be shared with third
              parties for marketing purposes.
            </p>
          </div>

          <div>
            <p className="mb-1">
              <strong>2- Types of SMS Communications:</strong> If you
              have consented to receive text messages from Ashlar
              Building Group, you may receive messages related to the
              following:
            </p>

            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>
                Appointment confirmations, appointment reminders,
                project updates, customer support responses, and
                service-related communications.
              </li>
            </ul>

            <p className="mt-2 ml-4 text-sm italic text-gray-600">
              Example: "Hello, this is a friendly reminder of your
              upcoming project with Ashlar Building Group. You can reply
              STOP to opt out of SMS messaging from Ashlar Building
              Group at any time."
            </p>
          </div>

          <div>
            <p>
              <strong>3- Message Frequency:</strong> Message frequency
              varies.
            </p>
          </div>

          <div>
            <p>
              <strong>4- Potential Fees for SMS Messaging:</strong>
              Please note that standard message and data rates may
              apply, depending on your carrier's pricing plan. These
              fees may vary if the message is sent domestically or
              internationally.
            </p>
          </div>

          <div>
            <p className="mb-1">
              <strong>5- Opt-In Method:</strong> You may opt-in to
              receive communications from Ashlar Building Group in the
              following ways:
            </p>

            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>By filling out a website form.</li>
            </ul>
          </div>

          <div>
            <p>
              <strong>6- Opt-Out Method:</strong> You can opt out of
              receiving SMS messages at any time. To do so, simply reply{" "}
              <strong>"STOP"</strong> to any SMS message you receive. If
              you wish to opt out of email communications, you may
              contact us directly to request removal from our messaging
              list via{" "}
              <a
                href="mailto:info@ashlarbuildinggroup.com"
                className="text-blue-600"
              >
                info@ashlarbuildinggroup.com
              </a>{" "}
              or call us at{" "}
              <a
                href="tel:+19256557741"
                className="text-blue-600"
              >
                (925) 655-7741
              </a>.
            </p>
          </div>

          <div>
            <p>
              <strong>7- Help:</strong> If you are experiencing any
              issues, you can reply with the keyword{" "}
              <strong>HELP</strong>. Or, you can get help directly from
              us at{" "}
              <a
                href="mailto:info@ashlarbuildinggroup.com"
                className="text-blue-600"
              >
                info@ashlarbuildinggroup.com
              </a>{" "}
              or call us at{" "}
              <a
                href="tel:+19256557741"
                className="text-blue-600"
              >
                (925) 655-7741
              </a>.
            </p>

            <p className="mt-2">
              <strong>Additional Options:</strong> If you do not wish to
              receive SMS messages, you can choose not to check the SMS
              consent box on our forms.
            </p>
          </div>

          <div>
            <p className="mb-1">
              <strong>8- Standard Messaging Disclosures:</strong>
            </p>

            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Message and data rates may apply.</li>
              <li>You can opt-out at any time by texting "STOP."</li>
              <li>Reply HELP for help.</li>
              <li>Message frequency varies.</li>
            </ul>
          </div>

        </div>

        <p className="mt-6 mb-2 text-gray-700">
          Your access to and use of the Website is conditional upon your
          acceptance of and compliance with this Privacy Policy. This
          Privacy Policy applies to everyone, including, but not limited
          to: visitors, users, and others, who wish to access or use
          the Website.
        </p>

        {/* Section 4 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          4. Email Communications
        </h2>

        <p className="mb-2">
          When you submit our contact form, you will receive an
          automated confirmation email acknowledging your inquiry. This
          email is transactional in nature and is not used for marketing
          purposes unless you separately opt in. Your email address will
          not be shared with third-party marketing platforms without
          your explicit consent.
        </p>

        {/* Section 5 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          5. Sharing of Information
        </h2>

        <p className="mb-2">
          We may share information with trusted Service Providers, to
          comply with laws, during mergers/acquisitions, or with your
          consent. We do not share personal data for marketing purposes.
        </p>

        {/* Section 6 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          6. International Data Transfers
        </h2>

        <p className="mb-2">
          Your data may be processed outside your country. We implement
          safeguards to protect your data in accordance with applicable
          laws.
        </p>

        {/* Section 7 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          7. Data Retention
        </h2>

        <p className="mb-2">
          Personal data is retained only as long as necessary for our
          purposes, legal compliance, or dispute resolution. You may
          request deletion of your data at any time by contacting us.
        </p>

        {/* Section 8 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          8. Your Privacy Rights
        </h2>

        <p className="mb-2">
          <strong>CCPA / CPRA (California Residents):</strong> You may
          know what data we collect, request access or deletion, opt out
          of selling or sharing your data (we do not sell data), limit
          use of sensitive information, and be free from discrimination
          for exercising your rights.
        </p>

        <p className="mb-2">
          <strong>GDPR (EU / EEA Residents):</strong> You may access,
          correct, delete your data, restrict or object to processing,
          withdraw consent, request portability, or lodge a complaint
          with a supervisory authority.
        </p>

        {/* Section 9 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          9. Data Security
        </h2>

        <p className="mb-2">
          We implement reasonable administrative, technical, and
          physical safeguards designed to protect personal information
          from unauthorized access, disclosure, alteration, or
          destruction.
        </p>

        <p className="mb-2">
          Our website uses SSL encryption to secure form submissions and
          transmitted information.
        </p>

        {/* Section 10 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          10. Children's Privacy
        </h2>

        <p className="mb-2">
          This website is not intended for children under the age of 13.
          We do not knowingly collect personal information from children
          under 13 years of age. If we become aware that such
          information has been collected, we will promptly delete it.
        </p>

        <p className="mb-2">
          By opting into our SMS services, you represent that you are at
          least 18 years of age.
        </p>

        {/* Section 11 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          11. Third-Party Links
        </h2>

        <p className="mb-2">
          We are not responsible for third-party websites linked from
          our site. Please review their privacy policies independently.
        </p>

        {/* Section 12 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          12. Changes to This Privacy Policy
        </h2>

        <p className="mb-2">
          Changes will be posted on this page with an updated date.
          Continued use of the Service after changes constitutes
          acceptance of the revised policy.
        </p>

        {/* Section 13 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          13. Contact Information
        </h2>

        <p className="mb-2">
          Email:{" "}
          <a
            href="mailto:info@ashlarbuildinggroup.com"
            className="text-blue-600"
          >
            info@ashlarbuildinggroup.com
          </a>
        </p>

        <p className="mb-2">
          Phone:{" "}
          <a
            href="tel:+19256557741"
            className="text-blue-600"
          >
            (925) 655-7741
          </a>
        </p>

        <p className="mb-2">
          Business Address:
          <br />
          Ashlar Building Group LLC
          <br />
          6200 Stoneridge Mall Rd, 3rd Floor
          <br />
          Pleasanton, CA 94588, United States
        </p>

        <p className="mb-2">
          Website:{" "}
          <a
            href="https://www.ashlarbuildinggroup.com"
            className="text-blue-600"
          >
            https://www.ashlarbuildinggroup.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;